import { Signal } from 'typed-signals';
import type { Popup } from 'popups/Popup';
import type { Bounds, Container } from 'pixi.js';
import type { LanguageKey } from '@softgames/localization';
import { getActiveLanguage, getSupportedLanguages, setLanguage } from '@softgames/localization';
import { SettingsPopupLayout } from '../../../../../layouts/popups/SettingsPopupLayout';
import type { PopupElementAnimationData } from '../../../../popups/PopupAnimations';
import type { App } from '../../../../app/App';
import { LanguageSelectorView } from '../../common/settings/LangugeSelectorView';
import { applyLayoutState } from '../../../../libs/layouts/populateLayouts/applyLayoutState';
import { SettingsSliderView } from '../../common/settings/SettingsSliderView';
import { soundManager, SoundTrackId } from '../../../../app/SoundManager';
import { copyToClipboard } from '../../../../app/utils/copyToClipboard';
import { CheatsEnabler } from '../../../../app/debug/CheatsEnabler';
import { displayDebugAlert } from '../../../../app/debug/debugAlert';

export interface SettingsPopupData {
  showHomeButton?: boolean;
}

export class SettingsPopup implements Popup {
  private readonly layout = new SettingsPopupLayout();

  public readonly root: Container = this.layout.root;

  public readonly initialBounds: Bounds = this.root.getLocalBounds();

  public readonly onEvent = new Signal<(event: string) => void>();

  private readonly languageSelector: LanguageSelectorView;

  constructor(private readonly app: App) {
    const { root, layout } = this;

    // eslint-disable-next-line no-new
    new CheatsEnabler(root, () => {
      if (this.app.platform.canUseCheats()) {
        displayDebugAlert('cheats enabled');
        app.tracking.trackCheatsEnabled();
        app.initDebugMenu();
        app.platform.tryEnableCheatMenu();
      }
    });

    this.initialBounds = root.getLocalBounds();

    layout.closeButton.onpointertap = (): void => {
      this.root.interactiveChildren = false;
      void this.languageSelector.hidePanel(0);
      this.onEvent.emit('close');
    };

    layout.homeButton.onpointertap = (): void => {
      this.root.interactiveChildren = false;
      void this.languageSelector.hidePanel(0);
      this.onEvent.emit('exitToMainMenu');
    };

    layout.retryButton.onpointertap = (): void => {
      this.root.interactiveChildren = false;
      void this.languageSelector.hidePanel(0);
      this.onEvent.emit('retry');
    };

    layout.playerId.onpointertap = async (): Promise<void> => {
      void copyToClipboard(app.utils.variableBank.getTextVariableValue('playerId', 'unknown'));
    };
    layout.playerId.cursor = 'default';

    this.languageSelector = this.initLanguageSelector();

    this.initSoundSlider();
    this.initMusicSlider();

    const supportEmail = this.app.platform.getSupportEmail();
    if (supportEmail) {
      layout.supportEmail.text = supportEmail;
    } else {
      applyLayoutState(layout, 'noSupportEmail');
    }
  }

  public onAdded(data: SettingsPopupData): void {
    const { layout } = this;
    if (!data.showHomeButton) {
      applyLayoutState(layout, 'mainMenu');
    }
  }

  private initSoundSlider(): void {
    const {
      app: { stateController: state },
      layout: { soundToggleButton },
    } = this;

    const slider = new SettingsSliderView({
      minValue: 0,
      maxValue: 100,
      initialValue: state.getSfxVolume(),
      onValueChanged: (volume: number): void => {
        soundManager.setVolume(volume, SoundTrackId.Sfx);
      },
      onValueFinalized: (volume: number): void => {
        state.setSfxVolume(volume);
      },
    }).props({
      parent: this.layout.soundToggleSlot,
      y: -12,
    });

    soundToggleButton.on('pointertap', () => {
      const value = slider.getValueRatio() === 0 ? 1 : 0;
      slider.setValueByRatio(value);
      state.setSfxVolume(value * 100);
    });
  }

  private initMusicSlider(): void {
    const {
      app: { stateController: state },
      layout: { musicToggleButton },
    } = this;

    const slider = new SettingsSliderView({
      minValue: 0,
      maxValue: 100,
      initialValue: state.getMusicVolume(),
      onValueChanged: (volume: number): void => {
        soundManager.setVolume(volume, SoundTrackId.Music);
      },
      onValueFinalized: (volume: number): void => {
        state.setMusicVolume(volume);
      },
    }).props({
      parent: this.layout.musicToggleSlot,
      y: -12,
    });

    musicToggleButton.on('pointertap', () => {
      const value = slider.getValueRatio() === 0 ? 1 : 0;
      slider.setValueByRatio(value);
      state.setMusicVolume(value * 100);
    });
  }

  private initLanguageSelector(): LanguageSelectorView {
    const languageSelector = new LanguageSelectorView({
      languageKeys: getSupportedLanguages(),
      getActiveLanguage: (): string => getActiveLanguage(),
      iconSize: { width: 74, height: 55 },
      iconPadding: 12,
      maxColumns: 3,
    }).props({
      parent: this.layout.languageSelectorSlot,
    });

    languageSelector.onLanguageSelected.connect((language: string) => {
      const languageKey = language as LanguageKey;
      setLanguage(languageKey);
      this.app.stateController.setLanguage(languageKey);
      this.onEvent.emit('languageChanged');
    });

    return languageSelector;
  }

  public getAnimatedElements(phase: 'in' | 'out'): PopupElementAnimationData[] {
    const { layout } = this;
    if (phase === 'in') {
      return [
        { container: layout.popup, scaleEase: 'backOut', duration: 400 },
        { container: layout.header, delay: 300, scale: { toX: 0.85, toY: 0.85 }, scaleEase: 'backOut', duration: 400 },
        {
          container: layout.settingsGroups,
          customAnimator: async (animationData: PopupElementAnimationData): Promise<void> => {
            await animationData.container.tweenFrom({ alpha: 0 }, 250, 'quadOut', true, 300).asyncOnComplete();
          },
        },
      ];
    }
    return [{ container: layout.popup }];
  }
}
